import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { WishlistProvider } from "@/lib/wishlist";
import MobileNav from "@/components/layout/MobileNav";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import VerticalSidebar from "@/components/layout/VerticalSidebar";
import Chatbot from "@/components/Chatbot";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Bespoke from "@/pages/Bespoke";
import BespokeHub from "@/pages/BespokeHub";
import BespokeShirt from "@/pages/BespokeShirt";
import BespokeSuit from "@/pages/BespokeSuit";
import BespokeTrousers from "@/pages/BespokeTrousers";
import BespokeKurta from "@/pages/BespokeKurta";
import BespokeGeneric from "@/pages/BespokeGeneric";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import UserDashboard from "@/pages/UserDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CorporateGifting from "@/pages/CorporateGifting";
import Lookbook from "@/pages/Lookbook";
import SeasonalSpice from "@/pages/SeasonalSpice";
import TrendingWear from "@/pages/TrendingWear";
import MarriageLook from "@/pages/MarriageLook";
import ShopByOccasion from "@/pages/ShopByOccasion";
import FAQ from "@/pages/FAQ";
import TrackOrder from "@/pages/TrackOrder";
import OrderConfirmation from "@/pages/OrderConfirmation";
import VirtualWardrobe from "@/pages/VirtualWardrobe";
import FabricCompare from "@/pages/FabricCompare";
import FabricsCatalog from "@/pages/FabricsCatalog";
import FabricDetail from "@/pages/FabricDetail";
import Orders from "@/pages/Orders";
import Wishlist from "@/pages/Wishlist";
import MasterManagerDashboard from "@/pages/MasterManagerDashboard";
import PackagingDashboard from "@/pages/PackagingDashboard";
import Returns from "@/pages/Returns";
import SizeFinder from "@/pages/SizeFinder";
import ShopTheLook from "@/pages/ShopTheLook";
import EventPlanner from "@/pages/EventPlanner";
import ScrollTop from "./components/ScrollTop";

function Layout({ children }) {
  const { pathname } = useLocation();
  // The admin console has its own full-height sidebar + header, so we drop the
  // storefront chrome (nav strip, header, footer, chat bubble) on /admin routes
  // — otherwise the fixed VerticalSidebar overlaps the admin's left menu.
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) {
    return (
      <div className="App">
        <ScrollToTop />
        {children}
      </div>
    );
  }
  return (
    <div className="App pb-16 md:pb-0">
      <Header />
      {/* <VerticalSidebar /> */}
      {children}
      <Footer />
      <ScrollToTop />
      <Chatbot />
      <MobileNav />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
        <ScrollTop/>
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/auth" element={<Layout><Auth /></Layout>} />

            <Route path="/fabrics" element={<Layout><FabricsCatalog /></Layout>} />
            <Route path="/fabric/:id" element={<Layout><FabricDetail /></Layout>} />
            <Route path="/accessories" element={<Layout><Catalog category="accessory" title="Accessories" /></Layout>} />
            <Route path="/shop/:key" element={<Layout><Catalog /></Layout>} />
            <Route path="/shop-by-occasion" element={<Layout><ShopByOccasion /></Layout>} />
            <Route path="/shop-by-occasion/:occasion" element={<Layout><Catalog /></Layout>} />

            <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
            <Route path="/bespoke" element={<Layout><BespokeHub /></Layout>} />
            <Route path="/bespoke/start" element={<Layout><Bespoke /></Layout>} />
            <Route path="/bespoke/shirt" element={<Layout><BespokeShirt /></Layout>} />
            <Route path="/bespoke/suit" element={<Layout><BespokeSuit /></Layout>} />
            <Route path="/bespoke/trousers" element={<Layout><BespokeTrousers /></Layout>} />
            <Route path="/bespoke/kurta" element={<Layout><BespokeKurta /></Layout>} />
            <Route path="/bespoke/:key" element={<Layout><BespokeGeneric /></Layout>} />
            <Route path="/cart" element={<Layout><Cart /></Layout>} />
            <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
            <Route path="/dashboard" element={<Layout><UserDashboard /></Layout>} />
            <Route path="/orders" element={<Layout><Orders /></Layout>} />
            <Route path="/wishlist" element={<Layout><Wishlist /></Layout>} />
            <Route path="/wardrobe" element={<Layout><VirtualWardrobe /></Layout>} />
            <Route path="/fabric-compare" element={<Layout><FabricCompare /></Layout>} />

            <Route path="/admin/*" element={<Layout><AdminDashboard /></Layout>} />

            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/corporate-gifting" element={<Layout><CorporateGifting /></Layout>} />
            <Route path="/lookbook" element={<Layout><Lookbook /></Layout>} />
            <Route path="/seasonal-spice" element={<Layout><SeasonalSpice /></Layout>} />
            <Route path="/trending-wear" element={<Layout><TrendingWear /></Layout>} />
            <Route path="/marriage-look" element={<Layout><MarriageLook /></Layout>} />
            <Route path="/faq" element={<Layout><FAQ /></Layout>} />
            <Route path="/track-order" element={<Layout><TrackOrder /></Layout>} />
            <Route path="/track-order/:id" element={<Layout><TrackOrder /></Layout>} />
            <Route path="/order-confirmed/:id" element={<Layout><OrderConfirmation /></Layout>} />
            <Route path="/order-confirmed" element={<Layout><OrderConfirmation /></Layout>} />

            <Route path="/master-manager" element={<Layout><MasterManagerDashboard /></Layout>} />
            <Route path="/packaging" element={<Layout><PackagingDashboard /></Layout>} />
            <Route path="/returns" element={<Layout><Returns /></Layout>} />
            <Route path="/size-finder" element={<Layout><SizeFinder /></Layout>} />
            <Route path="/shop-the-look" element={<Layout><ShopTheLook /></Layout>} />
            <Route path="/event-planner" element={<Layout><EventPlanner /></Layout>} />
          </Routes>
        </BrowserRouter>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}
